import React, {
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import {
  Button,
  Checkbox,
  DialogActions,
  DialogContent,
  FormControlLabel,
  Grid,
  Stack,
  Typography
} from '@mui/material';
import { saveAs } from 'file-saver';
import { useDispatch, useSelector } from 'react-redux';
import SelectField from 'src/common/formFields/SelectField';
import DatePickerField from 'src/common/formFields/DatePickerField';
import DateRangeShortcuts from 'src/common/formFields/DateRangeShortcuts';
import {
  Form, FormikProvider, useFormik
} from 'formik';
import { BASEAPI } from 'src/api/api';
import Loader from 'src/common/Loader';
import { getAllSites } from 'src/redux/siteSlice';
import { filterVehiclesBySite, getAllVehicles, getWailonVehicleList } from 'src/redux/vehicleSlice';
import {
  filterDispenserBySite,
  getAllDispensers
} from 'src/redux/dispenserSlice';
import { getAllVehicleGroupList } from 'src/redux/groupSlice';
import { DATE_FORMAT, REPORT_FILTER_OPTIONS } from 'src/utils/constants';
import { getAllTanks, filterTanksBySite } from 'src/redux/tankSlice';
import AutocompleteField from 'src/common/formFields/AutocompleteField';
import { getAllCards } from 'src/redux/cardSlice';
import useDidUpdate from 'src/hooks/useDidUpdate';
import { getCustomisedMileageList, getOtherReportsData } from 'src/redux/reportSlice';
import DownloadIcon from '@mui/icons-material/FileDownloadOutlined';
import getDefaultDateRangeValues from 'src/utils/dateDefaults';
import {
  getReportExportColumns
} from 'src/utils/exportColumns';
import { getFormattedReportURL, getReportQueryPayload } from './reportService';

const NativeDateAlignedField = ({ align, label, children }) => {
  if (!align) return children;

  return (
    <Stack spacing={0.75}>
      <Typography
        variant="caption"
        aria-hidden="true"
        sx={{
          letterSpacing: 0.4,
          textTransform: 'uppercase',
          visibility: 'hidden'
        }}
      >
        {label}
      </Typography>
      {children}
    </Stack>
  );
};

NativeDateAlignedField.propTypes = {
  align: PropTypes.bool,
  label: PropTypes.string,
  children: PropTypes.node
};

const getReportExportFileName = (reportTitle) => {
  if (reportTitle === 'customisedMileageReport') {
    return 'customised-mileage-report';
  }
  if (reportTitle === 'vehicleWiseFuelConsumptionReport') {
    return 'Vehicle-wise Fuel Consumption Report';
  }
  if (reportTitle === 'machineryDieselConsumptionReport') {
    return 'machinery-diesel-consumption-report';
  }

  return reportTitle.toLowerCase();
};

const expandMachineryDailyColumns = (columns, dateValue, includeGallons) => {
  if (!columns.includes('daily_details')) return columns;

  const selectedDate = moment(dateValue);
  if (!selectedDate.isValid()) {
    return columns.filter((column) => column !== 'daily_details');
  }

  const dailyColumns = [];
  for (let day = 1; day <= selectedDate.daysInMonth(); day += 1) {
    const dateColumn = selectedDate.clone().date(day).format('M/D/YYYY');
    dailyColumns.push(dateColumn);
    if (includeGallons) dailyColumns.push(`${dateColumn} Gallons`);
  }

  return columns.reduce((expandedColumns, column) => {
    if (column === 'daily_details') return [...expandedColumns, ...dailyColumns];
    return [...expandedColumns, column];
  }, []);
};

const getBackendErrorMessage = async (error) => {
  const errorResponse = error?.response || error;

  if (errorResponse?.status === 204) {
    return 'No data available! Try with different filters.';
  }

  const responseData = errorResponse?.data;
  if (!responseData) {
    return 'Error downloading the report. Try again later!';
  }

  const resolveMessageFromPayload = (payload) => {
    if (!payload) return '';
    if (typeof payload === 'string') return payload;
    if (Array.isArray(payload)) return payload.find(Boolean) || '';
    return payload.detail
      || payload.message
      || payload.error
      || payload.non_field_errors?.[0]
      || Object.values(payload).flat().find(Boolean)
      || '';
  };

  if (responseData instanceof Blob) {
    try {
      const text = await responseData.text();
      if (!text) return 'Error downloading the report. Try again later!';
      try {
        const parsed = JSON.parse(text);
        return resolveMessageFromPayload(parsed) || text;
      } catch (parseError) {
        return text;
      }
    } catch (blobError) {
      return 'Error downloading the report. Try again later!';
    }
  }

  return resolveMessageFromPayload(responseData) || 'Error downloading the report. Try again later!';
};

function ReportFilters({
  title,
  downloadUrl,
  viewUrl,
  filterConfig = REPORT_FILTER_OPTIONS,
  formModel,
  datePickerProps,
  formValidation,
  formInitValues,
  tableLimits,
  onViewReport,
  showViewButton = true,
  showTable = true,
  exportColumns,
  tableVariant = 'generic'
}) {
  const dispatch = useDispatch();
  const isViewDisabled = title === 'vehicleGroupWiseReport'
    || title === 'vehicleWiseFuelConsumptionReport';
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [hasViewedReport, setHasViewedReport] = useState(false);
  const usesNativeDateLayout = datePickerProps?.onlyDate
    && datePickerProps?.nativeInput !== false;

  const submitActionRef = useRef('export');
  const downloadReportRef = useRef(null);
  const { allSites } = useSelector((state) => state.sites);
  const { vehicleRegNumListBySite, wailonVehicleList } = useSelector((state) => state.vehicles);
  const { dispenserListBySite } = useSelector((state) => state.dispensers);
  const { allVehicleGroups } = useSelector((state) => state.groups);
  const { tankListBySite } = useSelector((state) => state.tanks);
  const { allCards } = useSelector((state) => state.cards);
  const { appSettings } = useSelector((state) => state.settings);

  const {
    formId,
    formField: {
      site,
      tank,
      month,
      year,
      address,
      assetType,
      requestStatus,
      cardNumber,
      vehicleGroup,
      vehicleNumber,
      wailonVehicleNumber,
      startTime,
      endTime
    }
  } = formModel;

  useEffect(() => {
    setError(false);
    if (filterConfig.site) {
      dispatch(getAllSites());
    }
    if (filterConfig.vehicle) {
      dispatch(getAllVehicles());
    }
    if (filterConfig.wailonVehicle) {
      dispatch(getWailonVehicleList());
    }
    if (filterConfig.dispenser) {
      dispatch(getAllDispensers());
    }
    if (filterConfig.vehicleGroup || title === 'vehicleGroupWiseReport') {
      dispatch(getAllVehicleGroupList());
    }
    if (filterConfig.tank) {
      dispatch(getAllTanks());
    }
    if (filterConfig.card) {
      dispatch(getAllCards());
    }
  }, [filterConfig, title]);

  const viewReportList = async (data, pageNo = 1, limit = 10) => {
    const API_URL = (viewUrl || downloadUrl).replace('export=true', 'export=false');
    const payload = {
      title, data, startTime, endTime, pageNo, limit
    };
    const apiURL = getFormattedReportURL(API_URL, payload);
    try {
      if (tableVariant === 'customisedMileage') {
        await dispatch(getCustomisedMileageList({
          limit,
          pageNo,
          params: getReportQueryPayload(payload)
        })).unwrap();
      } else {
        await dispatch(getOtherReportsData({ url: apiURL, reportName: title })).unwrap();
      }
    } catch (err) {
      setError('Error getting the report data. Try again later!');
    } finally {
      setLoading(false);
    }
  };

  const initialReportValues = useMemo(() => getDefaultDateRangeValues({
    initialValues: formInitValues,
    startName: startTime?.name,
    endName: endTime?.name,
    onlyDate: datePickerProps?.onlyDate
  }), [datePickerProps?.onlyDate, endTime?.name, formInitValues, startTime?.name]);

  const formikProps = useFormik({
    initialValues: initialReportValues,
    validationSchema: formValidation,
    enableReinitialize: true,
    onSubmit: (...args) => downloadReportRef.current?.(...args),
  });

  const resolvedExportColumns = useMemo(() => {
    if (exportColumns) return exportColumns;
    return getReportExportColumns(title, {
      dateValue: startTime?.name ? formikProps.values[startTime.name] : null,
      vehicleGroups: allVehicleGroups,
      includeGallons: Boolean(appSettings?.fuel_volume_unit)
    });
  }, [allVehicleGroups, appSettings?.fuel_volume_unit, exportColumns, formikProps.values,
    startTime?.name, title]);
  const [selectedColumns, setSelectedColumns] = useState(() => (
    resolvedExportColumns.map((column) => column.id)
  ));

  useEffect(() => {
    const nextSelectedColumns = resolvedExportColumns.map((column) => column.id);
    setSelectedColumns((currentSelectedColumns) => (
      currentSelectedColumns.length === nextSelectedColumns.length
      && currentSelectedColumns.every((column, index) => column === nextSelectedColumns[index])
        ? currentSelectedColumns
        : nextSelectedColumns
    ));
  }, [resolvedExportColumns]);

  async function downloadReport(data) {
    setLoading(true);
    setError(false);
    const isViewAction = submitActionRef.current === 'view';
    if (isViewAction) {
      onViewReport?.();
      viewReportList(data);
      return;
    }
    const payload = {
      title,
      data,
      startTime,
      endTime,
      columns: title === 'machineryDieselConsumptionReport'
        ? expandMachineryDailyColumns(
          selectedColumns,
          data[startTime?.name],
          Boolean(appSettings?.fuel_volume_unit)
        )
        : selectedColumns.filter((column) => (
          resolvedExportColumns.some((availableColumn) => availableColumn.id === column)
        ))
    };
    const apiURL = getFormattedReportURL(downloadUrl, payload, true);
    BASEAPI.get(apiURL, { responseType: 'blob', timeout: 20000 })
      .then((res) => {
        if (res.status !== 200) {
          throw res;
        }
        const fileName = `${getReportExportFileName(title)} ${moment().format(DATE_FORMAT.reportDateTime)}.xlsx`;
        saveAs(res.data, fileName);
      })
      .catch(async (err) => {
        const errMsg = await getBackendErrorMessage(err);
        setError(errMsg);
      })
      .finally(() => {
        setLoading(false);
      });
  }
  downloadReportRef.current = downloadReport;

  const isFormValidForAutoView = () => {
    try {
      return formValidation?.isValidSync(formikProps.values);
    } catch (err) {
      return false;
    }
  };

  const handleFormSubmit = (jsEvent) => {
    const { name } = jsEvent.nativeEvent.submitter;
    const isView = name === 'view';
    submitActionRef.current = name;
    if (isView) {
      setHasViewedReport(true);
    }
    return formikProps.handleSubmit(jsEvent);
  };

  // filter tank list based on site id on site change
  const onSiteChange = (selectedSiteId) => {
    if (filterConfig.vehicle) {
      dispatch(filterVehiclesBySite(selectedSiteId));
      formikProps.setFieldValue(vehicleNumber.name, 'all');
    }
    if (filterConfig.dispenser) {
      dispatch(filterDispenserBySite(selectedSiteId));
      formikProps.setFieldValue(address.name, 'all');
    }
    if (filterConfig.tank) {
      dispatch(filterTanksBySite(selectedSiteId));
      formikProps.setFieldValue(tank.name, 'all');
    }
  };

  useDidUpdate(() => {
    onSiteChange(formikProps.values[site?.name]);
  }, [formikProps.values[site?.name]]);

  useDidUpdate(() => {
    formikProps.resetForm();
    setHasViewedReport(false);
    if (startTime) { formikProps.setFieldValue(startTime.name, null); }
    if (endTime) { formikProps.setFieldValue(endTime.name, null); }
  }, [downloadUrl]);

  useDidUpdate(() => {
    if (!showTable) {
      return;
    }
    if (hasViewedReport) {
      viewReportList(formikProps.values, tableLimits?.pageNo, tableLimits?.limit);
    } else if (!showViewButton && isFormValidForAutoView()) {
      viewReportList(formikProps.values, tableLimits?.pageNo, tableLimits?.limit);
    }
  }, [showTable, showViewButton, tableLimits]);

  return (
    <FormikProvider value={formikProps}>
      <Form id={formId} onSubmit={handleFormSubmit}>
        {loading ? (
          <Loader />
        ) : (
          <DialogContent sx={{ mt: 2 }}>
            <Grid container spacing={3}>
              {filterConfig.site && (
                <Grid item xs={12} sm={4}>
                  <NativeDateAlignedField align={usesNativeDateLayout} label={site.label}>
                    <SelectField
                      {...site}
                      data={[...site.data, ...allSites]}
                      fullWidth
                    />
                  </NativeDateAlignedField>
                </Grid>
              )}
              {filterConfig.dispenser && (
                <Grid item xs={12} sm={4}>
                  <NativeDateAlignedField align={usesNativeDateLayout} label={address.label}>
                    <SelectField
                      {...address}
                      data={[...address.data, ...dispenserListBySite]}
                      fullWidth
                    />
                  </NativeDateAlignedField>
                </Grid>
              )}
              {filterConfig.tank && (
                <Grid item xs={12} sm={4}>
                  <NativeDateAlignedField align={usesNativeDateLayout} label={tank.label}>
                    <SelectField
                      {...tank}
                      data={[...tank.data, ...tankListBySite]}
                      fullWidth
                    />
                  </NativeDateAlignedField>
                </Grid>
              )}
              {filterConfig.card && (
                <Grid item xs={12} sm={4}>
                  <NativeDateAlignedField align={usesNativeDateLayout} label={cardNumber.label}>
                    <AutocompleteField
                      name={cardNumber.name}
                      label={cardNumber.label}
                      options={[...cardNumber.data, ...allCards]}
                      getOptionLabel={(option) => option.uid}
                      fullWidth
                    />
                  </NativeDateAlignedField>
                </Grid>
              )}
              {filterConfig.wailonVehicle && (
                <Grid item xs={12} sm={4}>
                  <NativeDateAlignedField align={usesNativeDateLayout} label={wailonVehicleNumber.label}>
                    <AutocompleteField
                      name={wailonVehicleNumber.name}
                      label={wailonVehicleNumber.label}
                      options={wailonVehicleList}
                      getOptionLabel={(option) => option?.registration_num}
                      fullWidth
                    />
                  </NativeDateAlignedField>
                </Grid>
              )}
              {filterConfig.vehicle && (
                <Grid item xs={12} sm={4}>
                  <NativeDateAlignedField align={usesNativeDateLayout} label={vehicleNumber.label}>
                    <AutocompleteField
                      name={vehicleNumber.name}
                      label={vehicleNumber.label}
                      options={[...vehicleNumber.data, ...vehicleRegNumListBySite]}
                      getOptionLabel={(option) => option.registration_num}
                      fullWidth
                    />
                  </NativeDateAlignedField>
                </Grid>
              )}
              {filterConfig.vehicleGroup && (
                <Grid item xs={12} sm={4}>
                  <NativeDateAlignedField align={usesNativeDateLayout} label={vehicleGroup.label}>
                    <SelectField
                      {...vehicleGroup}
                      data={[...vehicleGroup.data, ...allVehicleGroups]}
                      fullWidth
                    />
                  </NativeDateAlignedField>
                </Grid>
              )}
              {filterConfig.assetType && (
                <Grid item xs={12} sm={4}>
                  <NativeDateAlignedField align={usesNativeDateLayout} label={assetType.label}>
                    <SelectField
                      {...assetType}
                      data={assetType.data}
                      fullWidth
                    />
                  </NativeDateAlignedField>
                </Grid>
              )}
              {filterConfig.requestStatus && (
                <Grid item xs={12} sm={4}>
                  <NativeDateAlignedField align={usesNativeDateLayout} label={requestStatus.label}>
                    <SelectField
                      {...requestStatus}
                      fullWidth
                    />
                  </NativeDateAlignedField>
                </Grid>
              )}
              {month && (
                <Grid item xs={12} sm={4}>
                  <NativeDateAlignedField align={usesNativeDateLayout} label={month.label}>
                    <SelectField
                      {...month}
                      data={month.data}
                      fullWidth
                      required
                    />
                  </NativeDateAlignedField>
                </Grid>
              )}
              {year && (
                <Grid item xs={12} sm={4}>
                  <NativeDateAlignedField align={usesNativeDateLayout} label={year.label}>
                    <SelectField
                      {...year}
                      data={year.data}
                      fullWidth
                      required
                    />
                  </NativeDateAlignedField>
                </Grid>
              )}
              {startTime && (
                <Grid item xs={12} sm={4}>
                  <DatePickerField
                    {...startTime}
                    {...datePickerProps}
                    required
                    disableFuture
                    nativeInput={datePickerProps?.nativeInput ?? true}
                  />
                </Grid>
              )}
              {endTime && (
                <Grid item xs={12} sm={4}>
                  <DatePickerField
                    {...endTime}
                    {...datePickerProps}
                    required
                    disableFuture
                    nativeInput={datePickerProps?.nativeInput ?? true}
                  />
                </Grid>
              )}
              {startTime && endTime && (
                <Grid item xs={12}>
                  <DateRangeShortcuts
                    startName={startTime.name}
                    endName={endTime.name}
                    onlyDate={datePickerProps?.onlyDate}
                    dateFormat={datePickerProps?.dateFormat}
                  />
                </Grid>
              )}
              {resolvedExportColumns.length > 0 && (
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mt: 2, mb: 0.5 }}>
                  Columns to export
                </Typography>
                <Grid container>
                  {resolvedExportColumns.map((column) => (
                    <Grid item xs={12} sm={6} md={4} key={column.id}>
                      <FormControlLabel
                        control={(
                          <Checkbox
                            size="small"
                            checked={selectedColumns.includes(column.id)}
                            onChange={() => setSelectedColumns((current) => (
                              current.includes(column.id)
                                ? current.filter((id) => id !== column.id)
                                : [...current, column.id]
                            ))}
                          />
                        )}
                        label={column.label}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Grid>
              )}
            </Grid>
          </DialogContent>
        )}
        {error && (
        <Typography
          variant="subtitle1"
          sx={{ color: 'red', pt: 2, textAlign: 'center' }}
        >
          {error || ''}
        </Typography>
        )}
        <DialogActions
          sx={{
            m: 2,
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 2,
            '& > *': {
              width: { xs: '100%', sm: 'auto' },
              m: 0
            }
          }}
        >
          {showViewButton && (
          <Button
            className="report-view-button"
            color="primary"
            type="submit"
            name="view"
            disabled={formikProps.isSubmitting || isViewDisabled}
            size="large"
            variant="contained"
          >
            View Report
          </Button>
          )}
          <Button
            color="primary"
            type="submit"
            name="export"
            disabled={formikProps.isSubmitting}
            size="large"
            startIcon={<DownloadIcon />}
            variant="contained"
            sx={{
              backgroundColor: '#0B6E99',
              border: '1px solid #0B6E99',
              '&:hover': {
                backgroundColor: '#095A7D',
                borderColor: '#095A7D'
              }
            }}
          >
            Export
          </Button>
        </DialogActions>
      </Form>
    </FormikProvider>
  );
}

ReportFilters.propTypes = {
  filterConfig: PropTypes.object,
  datePickerProps: PropTypes.object,
  formModel: PropTypes.object.isRequired,
  formValidation: PropTypes.object.isRequired,
  formInitValues: PropTypes.object.isRequired,
  title: PropTypes.string.isRequired,
  isViewReport: PropTypes.bool,
  tableLimits: PropTypes.object,
  onViewReport: PropTypes.func,
  downloadUrl: PropTypes.string.isRequired,
  viewUrl: PropTypes.string,
  tableVariant: PropTypes.string,
  showViewButton: PropTypes.bool,
  showTable: PropTypes.bool,
  exportColumns: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired
  }))
};

export default ReportFilters;
